import { useState } from 'react';
import {
  Receipt, Upload, CheckCircle2, Info, Percent, CalendarClock, Landmark,
  Repeat, AlertTriangle,
} from 'lucide-react';
import { api, ApiError, fileToUpload } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useApi } from '@/lib/useApi';
import { money, date, dateTime, humanise } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, EmptyState, Field, Input, Modal, MoneyInput,
  PageHeader, Select, Textarea, useToast, statusTone,
} from '@/components/ui';
import { usePortal } from './PortalShell';
import type { PaymentSchedule, RentCycle } from '@shared/types';

const METHODS = ['bank_transfer', 'mobile_money', 'cash', 'card', 'cheque', 'other'];

interface HowToPay {
  currency: string;
  payment_method: 'manual' | 'scheduled';
  rent_cents: number;
  cycle: string | null;
  next_due_date: string | null;
  outstanding_cents: number;
  deposit_outstanding_cents: number;
  rent_outstanding_cents: number;
  instructions: string | null;
  reference_note: string | null;
  payment_reference: string | null;
  schedule: PaymentSchedule | null;
  deposit: {
    total_cents: number; held_cents: number;
    scheme: string | null; scheme_reference: string | null; protected_at: string | null;
  } | null;
}

/**
 * Where to send the money, and how much.
 *
 * The single most common question a tenant has, and until now the portal
 * answered none of it — it showed a balance and no way to settle it. The
 * details are the landlord's own; nothing is collected here.
 */
function HowToPayCard({ data }: { data: HowToPay }) {

  const scheduled = data.payment_method === 'scheduled';
  const cycleWord = data.cycle === 'weekly' ? 'week'
    : data.cycle === 'quarterly' ? 'quarter'
    : data.cycle === 'yearly' ? 'year'
    : 'month';

  return (
    <Card>
      <CardHeader
        title={<span className="flex items-center gap-2"><Landmark className="h-5 w-5" /> How to pay</span>}
        subtitle={scheduled
          ? 'You pay by standing order'
          : 'Send it yourself, then tell us below'}
      />
      <div className="p-5 sm:p-6 pt-4 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-faint uppercase tracking-wide">Rent</p>
            <p className="text-lg font-semibold text-ink tnum mt-0.5">
              {money(data.rent_cents, data.currency)}
            </p>
            <p className="text-sm text-muted">per {cycleWord}</p>
          </div>
          <div>
            <p className="text-xs text-faint uppercase tracking-wide">Owing now</p>
            <p className="text-lg font-semibold text-ink tnum mt-0.5">
              {money(data.rent_outstanding_cents, data.currency)}
            </p>
            {data.next_due_date && (
              <p className="text-sm text-muted">next due {date(data.next_due_date)}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-faint uppercase tracking-wide">Bond owing</p>
            <p className="text-lg font-semibold text-ink tnum mt-0.5">
              {money(data.deposit_outstanding_cents, data.currency)}
            </p>
            {data.deposit?.scheme && (
              <p className="text-sm text-muted">held with {data.deposit.scheme}</p>
            )}
          </div>
        </div>

        {data.instructions ? (
          <div className="rounded-xl border border-line bg-raised p-4">
            <p className="text-xs text-faint uppercase tracking-wide mb-2">Send payment to</p>
            <pre className="text-sm text-ink whitespace-pre-wrap font-mono leading-relaxed">
              {data.instructions}
            </pre>
            {/*
              The reference gets its own line and monospace, because it is the
              one thing a tenant can get wrong that makes their payment arrive
              and still look like nobody paid.
            */}
            {data.payment_reference && (
              <div className="mt-3 pt-3 border-t border-line">
                <p className="text-xs text-faint uppercase tracking-wide mb-1">
                  Use this reference
                </p>
                <p className="font-mono text-base text-ink font-semibold break-all">
                  {data.payment_reference}
                </p>
                <p className="text-xs text-muted mt-1">
                  Type it into the reference box when you set up the payment. It is how
                  your landlord tells your rent apart from everyone else&rsquo;s.
                </p>
              </div>
            )}
            {data.reference_note && (
              <p className="text-sm text-muted mt-3 pt-3 border-t border-line">
                <strong className="text-ink">Also:</strong> {data.reference_note}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted">
            Your property manager has not added payment details yet. Please ask them
            where to send it.
          </p>
        )}

        {scheduled ? (
          <p className="text-sm text-muted leading-relaxed">
            Your rent is set up as a standing order with your bank, so there is nothing
            for you to do each {cycleWord}. If you cancel or change it, tell your property
            manager — otherwise it will look as though the rent has not arrived.
          </p>
        ) : (
          <p className="text-sm text-muted leading-relaxed">
            Once you have sent it, use <strong className="text-ink">Tell us you&rsquo;ve paid</strong> above
            and attach a receipt. It counts once your property manager has checked it.
          </p>
        )}

        {data.deposit?.protected_at && (
          <p className="text-sm text-muted pt-3 border-t border-line">
            Your bond is protected with {data.deposit.scheme}
            {data.deposit.scheme_reference ? ` (ref ${data.deposit.scheme_reference})` : ''},
            since {date(data.deposit.protected_at)}.
          </p>
        )}
      </div>

    </Card>
  );
}

/**
 * "I have set one up with my bank."
 *
 * ── Why this is a statement and not a switch ──────────────────────────────
 *
 * Ticking it does not move the tenant off the claim-with-proof path. Their
 * landlord confirms it first, because that path is the one that asks for
 * evidence and the tenant is the party with an interest in leaving it.
 *
 * And confirming still does not mark anything paid. A standing order tells a
 * bank to pay; only a statement shows that it did. Saying so plainly here is
 * cheaper than the message a month later asking why the rent still shows as
 * due.
 */
function StandingOrderCard({ howToPay, onChange }: {
  howToPay: HowToPay;
  onChange: () => void;
}) {
  const toast = useToast();
  const { reload } = usePortal();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const schedule = howToPay.schedule;
  const [form, setForm] = useState({
    cycle: (howToPay.cycle ?? 'monthly') as RentCycle,
    amount_cents: howToPay.rent_cents,
    first_payment_date: '',
    bank_name: '',
    note: '',
  });

  const cycleWord = (c: string | null) => (c === 'weekly' ? 'week'
    : c === 'quarterly' ? 'quarter' : c === 'yearly' ? 'year' : 'month');

  async function declare() {
    setBusy(true);
    try {
      await api.post('/portal/payment-schedule', {
        ...form,
        first_payment_date: form.first_payment_date || undefined,
      });
      toast.success('Thank you — your landlord will confirm it.');
      setOpen(false);
      onChange();
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be saved');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await api.post('/portal/payment-schedule/cancel', {});
      toast.success('Noted. You will need to send payments yourself again.');
      onChange();
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be saved');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={(
          <span className="flex items-center gap-2">
            <Repeat className="h-5 w-5" /> Paying automatically
          </span>
        )}
        subtitle={schedule
          ? 'What you have told your landlord'
          : 'If your bank pays your rent for you, say so here'}
      />
      <div className="p-5 sm:p-6 pt-4 space-y-4">
        {schedule ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={schedule.status === 'confirmed' ? 'ok' : 'info'}>
                {schedule.status === 'confirmed' ? 'Confirmed by your landlord'
                  : 'Waiting for your landlord to confirm'}
              </Badge>
              <span className="text-sm text-muted">
                {money(schedule.amount_cents, howToPay.currency)} every {cycleWord(schedule.cycle)}
                {schedule.first_payment_date ? `, from ${date(schedule.first_payment_date)}` : ''}
              </span>
            </div>

            {schedule.reference && (
              <p className="text-sm text-muted">
                Quoting <span className="font-mono text-ink">{schedule.reference}</span>
              </p>
            )}

            {/*
              Said before it becomes an argument about arrears. The tolerance in
              the backend means this only fires on a real difference, not on the
              rounding between 52 weeks and 12 months.
            */}
            {schedule.mismatch && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-warn/10 border border-warn/25">
                <AlertTriangle className="h-5 w-5 text-warn shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium text-ink">
                    That does not add up to your rent over a year
                  </p>
                  <p className="text-muted leading-relaxed">
                    What you have set up comes to{' '}
                    {money(schedule.mismatch.declared_per_year_cents, howToPay.currency)} a year,
                    and your rent is{' '}
                    {money(schedule.mismatch.expected_per_year_cents, howToPay.currency)}.
                    {schedule.mismatch.short
                      ? ' You would fall behind by '
                      : ' You would overpay by '}
                    {money(
                      Math.abs(schedule.mismatch.difference_per_year_cents), howToPay.currency,
                    )} a year. Check the amount with your bank, or talk to your landlord.
                  </p>
                </div>
              </div>
            )}

            {schedule.status === 'confirmed' && (
              <p className="text-sm text-muted leading-relaxed">
                Your rent will still show as due until your landlord has checked it
                arrived. That is normal — a standing order tells your bank to pay, and
                only their statement shows that it did.
              </p>
            )}

            <Button onClick={() => void cancel()} loading={busy}>
              I have cancelled it
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted leading-relaxed">
              A standing order is an instruction you set up with your own bank to pay
              the same amount every {cycleWord(howToPay.cycle)}. If you have one, tell
              your landlord here and you will not need to send proof of every payment.
            </p>
            <Button variant="primary" onClick={() => setOpen(true)}>
              I pay by standing order
            </Button>
          </>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Tell your landlord about your standing order"
        subtitle="They will confirm it. Nothing changes until they do."
        footer={(
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => void declare()} loading={busy}>Send</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="How much" hint="What your bank sends each time">
              <MoneyInput
                valueCents={form.amount_cents}
                onChangeCents={(v) => setForm((f) => ({ ...f, amount_cents: v }))}
                currency={howToPay.currency}
              />
            </Field>
            <Field label="How often">
              <Select
                value={form.cycle}
                onChange={(e) => setForm((f) => ({ ...f, cycle: e.target.value as RentCycle }))}
              >
                <option value="weekly">Every week</option>
                <option value="monthly">Every month</option>
                <option value="quarterly">Every quarter</option>
                <option value="yearly">Every year</option>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First payment" hint="Or the next one, if it has started">
              <Input
                type="date"
                value={form.first_payment_date}
                onChange={(e) => setForm((f) => ({ ...f, first_payment_date: e.target.value }))}
              />
            </Field>
            <Field label="Which bank" hint="Optional">
              <Input
                value={form.bank_name}
                onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                placeholder="Barclays"
              />
            </Field>
          </div>

          {howToPay.payment_reference && (
            <p className="text-sm text-muted">
              Make sure the reference on it is{' '}
              <span className="font-mono text-ink">{howToPay.payment_reference}</span>, or
              your payments may not be matched to you.
            </p>
          )}

          <Field label="Anything else they should know" hint="Optional">
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="It comes out on the 28th because that is when I am paid."
            />
          </Field>
        </div>
      </Modal>
    </Card>
  );
}

export default function PortalPayments() {
  const { data, reload } = usePortal();
  const { currency } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const openCharges = data.charges.filter((c) => (c.balance_cents ?? 0) > 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rent"
        subtitle="What you owe, what you have paid, and how it was verified"
        action={
          <Button variant="primary" icon={<Upload className="h-4 w-4" />} onClick={() => setSubmitting(true)}>
            Tell us you've paid
          </Button>
        }
      />

      <PayingCards />

      {/* How verification works — sets expectations before anyone submits. */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-info/8 border border-info/25">
        <Info className="h-5 w-5 text-info shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-ink">How this works</p>
          <p className="text-muted mt-1 leading-relaxed">
            When you tell us you've paid, it is recorded straight away but marked as
            "being checked". Your landlord confirms it against their bank or records, and only then
            does your balance go down. Attaching a photo of your receipt makes that much faster.
          </p>
        </div>
      </div>

      {openCharges.length > 0 && (
        <Card>
          <CardHeader title="Outstanding" />
          <div className="p-5 sm:p-6 pt-4 space-y-2">
            {openCharges.map((c) => {
              const due = c.effective_due_date ?? c.due_date;
              const isLate = new Date(due) < new Date();
              return (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{c.description}</p>
                    <p className="text-xs text-faint">
                      Due {date(due)}
                      {c.waived_cents > 0 && (
                        <span className="text-ok"> · {money(c.waived_cents, currency)} reduced</span>
                      )}
                    </p>
                  </div>
                  {(c.pending_cents ?? 0) > 0 && <Badge tone="warn">Being checked</Badge>}
                  <span className={`font-bold tnum shrink-0 ${isLate ? 'text-danger' : ''}`}>
                    {money(c.balance_cents ?? 0, currency)}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Concessions the landlord has granted. */}
      {data.adjustments.length > 0 && (
        <Card>
          <CardHeader title="Agreed changes" subtitle="Extensions and reductions on your account" />
          <div className="p-5 sm:p-6 pt-4 space-y-3">
            {data.adjustments.map((a) => (
              <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl border border-line bg-raised/40">
                <div className="h-8 w-8 rounded-lg bg-surface border border-line flex items-center
                                justify-center shrink-0 text-muted">
                  {a.kind === 'extension'
                    ? <CalendarClock className="h-4 w-4" />
                    : <Percent className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge tone={a.kind === 'extension' ? 'info' : 'ok'}>{humanise(a.kind)}</Badge>
                    {a.charge_description && (
                      <span className="text-xs text-faint truncate">{a.charge_description}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted mt-1.5 leading-relaxed">{a.reason}</p>
                  <p className="text-xs text-faint mt-1">{dateTime(a.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  {a.new_due_date ? (
                    <span className="text-sm font-semibold">{date(a.new_due_date)}</span>
                  ) : a.amount_cents !== 0 ? (
                    <span className="text-sm font-semibold text-ok tnum">
                      −{money(Math.abs(a.amount_cents), currency)}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Payment history" />
        <div className="p-5 sm:p-6 pt-4">
          {data.payments.length === 0 ? (
            <EmptyState
              icon={<Receipt className="h-6 w-6" />}
              title="No payments yet"
              message="Once you tell us about a payment it will show here with its status."
              action={<Button variant="primary" size="sm" onClick={() => setSubmitting(true)}>Tell us you've paid</Button>}
            />
          ) : (
            <div className="space-y-3">
              {data.payments.map((p) => (
                <div key={p.id} className="p-4 rounded-xl border border-line bg-raised/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold tnum text-lg">{money(p.amount_cents, currency)}</p>
                      <p className="text-xs text-faint mt-0.5">
                        {date(p.paid_at)} · {humanise(p.method)}
                        {p.reference ? ` · ${p.reference}` : ''}
                      </p>
                      {p.charge_description && (
                        <p className="text-xs text-muted mt-1">For: {p.charge_description}</p>
                      )}
                    </div>
                    <Badge tone={statusTone(p.status)} dot>
                      {p.status === 'pending' ? 'Being checked' : humanise(p.status)}
                    </Badge>
                  </div>

                  {p.tenant_note && (
                    <p className="text-sm text-muted italic mt-2">"{p.tenant_note}"</p>
                  )}

                  {p.status === 'verified' && p.verified_at && (
                    <p className="flex items-center gap-1.5 text-xs text-ok mt-2">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Confirmed on {date(p.verified_at)}
                    </p>
                  )}

                  {p.status === 'rejected' && p.rejection_reason && (
                    <div className="mt-2 p-2.5 rounded-lg bg-danger/10 border border-danger/25">
                      <p className="text-xs text-danger">
                        <span className="font-semibold">Could not be confirmed:</span> {p.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {submitting && (
        <SubmitPaymentModal onClose={() => setSubmitting(false)} onDone={() => { setSubmitting(false); reload(); }} />
      )}
    </div>
  );
}

function SubmitPaymentModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { data } = usePortal();
  const { currency } = useAuth();
  const toast = useToast();

  const [busy, setBusy] = useState(false);
  const [chargeId, setChargeId] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [proof, setProof] = useState<File | null>(null);

  const openCharges = data.charges.filter((c) => (c.balance_cents ?? 0) > 0);

  function pickCharge(id: string) {
    setChargeId(id);
    const c = openCharges.find((x) => x.id === id);
    if (c?.balance_cents) setAmountCents(c.balance_cents);
  }

  async function submit() {
    if (amountCents <= 0) { toast.error('Enter how much you paid'); return; }

    setBusy(true);
    try {
      const payment = await api.post<{ id: string }>('/portal/payments', {
        charge_id: chargeId || null,
        amount_cents: amountCents,
        method,
        reference: reference || null,
        paid_at: paidAt,
        tenant_note: note || null,
      });

      // Attach the receipt separately so a failed upload doesn't lose the claim.
      if (proof) {
        try {
          const payload = await fileToUpload(proof);
          await api.post(`/portal/payments/${payment.id}/proof`, payload);
        } catch {
          toast.info('Payment recorded, but the receipt could not be uploaded. You can send it to your landlord directly.');
          onDone();
          return;
        }
      }

      toast.success('Thank you — your landlord will confirm it shortly');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not record that payment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Tell us you've paid"
      subtitle="Your landlord will check it against their records"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Submit</Button>
        </>
      }
    >
      <div className="space-y-4">
        {openCharges.length > 0 && (
          <Field label="What is this for?" hint="Helps your landlord match it up quickly">
            <Select value={chargeId} onChange={(e) => pickCharge(e.target.value)}>
              <option value="">Not sure / general payment</option>
              {openCharges.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.description} — {money(c.balance_cents ?? 0, currency)} outstanding
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="How much did you pay?" required>
            <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
          </Field>
          <Field label="When did you pay?" required>
            <Input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="How did you pay?">
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{humanise(m)}</option>)}
            </Select>
          </Field>
          <Field label="Reference" hint="Transaction or receipt number">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="MPESA-QF7723L" />
          </Field>
        </div>

        <Field
          label="Photo of your receipt"
          hint="Strongly recommended — payments with proof are confirmed much faster"
        >
          <label className="block border-2 border-dashed border-line rounded-2xl p-5 text-center
                            cursor-pointer hover:border-brand/50 hover:bg-raised/40 transition-colors">
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setProof(e.target.files?.[0] ?? null)}
            />
            {proof ? (
              <span className="flex items-center justify-center gap-2 text-sm text-brand font-medium">
                <CheckCircle2 className="h-4 w-4" />
                {proof.name}
              </span>
            ) : (
              <span className="flex flex-col items-center gap-1.5">
                <Upload className="h-5 w-5 text-faint" />
                <span className="text-sm text-muted">Tap to add a photo</span>
              </span>
            )}
          </label>
        </Field>

        <Field label="Anything to add?">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Paid this morning by mobile money."
          />
        </Field>
      </div>
    </Modal>
  );
}


/**
 * How to pay, and how they have arranged to.
 *
 * One fetch feeding both: declaring a standing order changes what "how to pay"
 * should say, so the two cannot be allowed to disagree by being loaded
 * separately.
 */
function PayingCards() {
  const { data, reload } = useApi<HowToPay>('/portal/how-to-pay');
  if (!data) return null;

  return (
    <>
      <HowToPayCard data={data} />
      <StandingOrderCard howToPay={data} onChange={reload} />
    </>
  );
}
